import { School, ApiResponse } from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';

class SchoolService {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async getAllSchools(): Promise<ApiResponse<School[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch schools');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch schools');
    }
  }

  async getSchoolById(id: string): Promise<ApiResponse<School>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch school');
    }
  }

  async createSchool(schoolData: Partial<School>): Promise<ApiResponse<School>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(schoolData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to create school');
    }
  }

  async updateSchool(id: string, schoolData: Partial<School>): Promise<ApiResponse<School>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(schoolData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update school');
    }
  }

  async deleteSchool(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete school');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete school');
    }
  }

  async searchSchools(name: string): Promise<ApiResponse<School[]>> {
    try {
      const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools/search?name=${encodeURIComponent(name)}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to search schools');
      }

      return data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to search schools');
    }
  }

  async exportSchools(): Promise<Blob> {
    const response = await fetch(`${API_ENDPOINTS.schoolApi}/schools/export`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to export schools');
    }
    return await response.blob();
  }
}

const schoolService = new SchoolService();
export default schoolService;
